import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface SkillData {
  skill: string;
  score: number;
}

interface SkillsRadarChartProps {
  skills: SkillData[];
  title?: string;
  color?: string;
}

const SkillsRadarChart: React.FC<SkillsRadarChartProps> = ({
  skills,
  title = 'Skills Assessment',
  color = '#0891b2'
}) => {
  if (!skills || skills.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          <p>No skills data available for visualization</p>
        </div>
      </div>
    );
  }

  const chartData = skills.map(skill => ({
    skill: skill.skill,
    value: skill.score,
    fullMark: 100
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
      {skills.length < 3 && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            Limited skills data available. For best results, ensure your resume contains detailed skills information.
          </p>
        </div>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={chartData}>
          <PolarGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="skill"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickLine={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            tickCount={6}
          />
          <Radar
            name="Proficiency"
            dataKey="value"
            stroke={color}
            fill={color}
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            formatter={(value: number) => [`${value}%`, 'Proficiency']}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SkillsRadarChart;
